import { tWithKeyAttr as t } from "../../../i18nHelper";
import loginStyles from "../../../pages/login/login.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { loginEmailValidationSchema } from "../../../utils/validation";
import {
  useLoginMutation,
  useLoginWithCodeMutation,
} from "../../../services/api/auth.api";
import {
  errorUserSelector,
  errorTimeStampSelector,
} from "../../../services/selectors/auth.selectors";
import { useAppSelector } from "../../../services/hooks";
import { useActions } from "../../../services/hooks/useActions";
import { useEffect, useRef, useState } from "react";

interface FormInputRefs {
  email: HTMLInputElement | null;
  password: HTMLInputElement | null;
}

function LoginEmailForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const from: string = location.state?.from || "/main";

  const inputRefs = useRef<FormInputRefs>({
    email: null,
    password: null,
  });

  const [login, { isError, isLoading: isSendingEmailAndPassword }] =
    useLoginMutation();
  const [loginWithCode, { isLoading: isSendingCode }] =
    useLoginWithCodeMutation();

  const loginError = useAppSelector(errorUserSelector);
  const retryAfterTimestamp = useAppSelector(errorTimeStampSelector);
  const isLoading = isSendingEmailAndPassword || isSendingCode;

  const { setPhone, setLoginInfo, clearRetryAfter } = useActions();

  const [countdown, setCountdown] = useState<number | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const handleLogin = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    const response = await login({ email, password, roleId: "2" });
    if ("data" in response && response.data.success) {
      const { user, accessToken } = response.data;
      if (accessToken) {
        navigate("/");
      } else {
        const phoneNum = user?.Phone ?? user?.phone;
        const id = user?.Id ?? user?.id;
        if (!phoneNum || !id) {
          return;
        }
        setLoginInfo({ email, password, id });
        setPhone(phoneNum);
       
        loginWithCode({ phone: phoneNum, roleId: 2, id }).then(
          (response) => {
            if ("data" in response && response.data.success) {
              navigate("/enter_code", { state: { from }, replace: true });
            }
          }
        );
      }
    }
  };

  const handleChangeWithTouch = (e: React.ChangeEvent<any>) => {
    formik.setFieldTouched(e.target.type, true, false);
    formik.handleChange(e);
  };
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      roleId: "2",
      id: 0,
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: loginEmailValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleLogin(values);
      setSubmitting(false);
    },
  });

  // Handle autofill
  useEffect(() => {
    const syncAutofillValues = () => {
      const { email, password } = inputRefs.current;
      let hasChanges = false;

      if (email?.value && email.value !== formik.values.email) {
        formik.setFieldValue("email", email.value);
        hasChanges = true;
      }

      if (password?.value && password.value !== formik.values.password) {
        formik.setFieldValue("password", password.value);
        hasChanges = true;
      }

      // If values were autofilled, mark fields as touched
      if (hasChanges) {
        const touchedFields: { [key: string]: boolean } = {};
        if (email?.value) touchedFields.email = true;
        if (password?.value) touchedFields.password = true;
        formik.setTouched(touchedFields);
      }
    };

    const timeoutId = setTimeout(syncAutofillValues, 100);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (retryAfterTimestamp) {
      const interval = setInterval(() => {
        const secondsLeft = Math.max(
          0,
          Math.floor((retryAfterTimestamp - Date.now()) / 1000)
        );
        setCountdown(secondsLeft);

        if (secondsLeft <= 0) {
          clearInterval(interval);
          setCountdown(null);
          clearRetryAfter(); // Custom action to reset retryAfterTimestamp
        }
      }, 1000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryAfterTimestamp]);

  // Check if form is completely filled and valid
  const isFormComplete = Boolean(
    formik.values.email &&
    formik.values.password &&
    !formik.errors.email &&
    !formik.errors.password 
  );

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  return (
    <form onSubmit={formik.handleSubmit} className={loginStyles.login__form} data-testid="login-therapist-page">
      <input
        ref={(el) => (inputRefs.current.email = el)}
        type="email"
        required
        className={`${loginStyles.login__input} ${loginStyles.login__input_email
          } 
          ${formik.errors.email &&
          formik.touched.email &&
          loginStyles.login__input_invalid
          }`}
        name="email"
        id="email"
        data-testid="email"
        placeholder={t("EMAIL")}
        value={formik.values.email}
        onChange={handleChangeWithTouch}
        onBlur={formik.handleBlur}
        autoComplete="email"
      />
    <div className="inputContainer">
      <input
      type={passwordVisible ? "text" : "password"}    
        ref={(el) => (inputRefs.current.password = el)}
        required
        className={`${loginStyles.login__input} 
          ${formik.errors.password &&
          formik.touched.password &&
          loginStyles.login__input_invalid
          }`}
        name="password"
        id="password"
        data-testid="password"
        placeholder={t('CHANGE_PASSWORD_LABEL_PASSWORD')}
        value={formik.values.password}
        onChange={handleChangeWithTouch}
        onBlur={formik.handleBlur}
        autoComplete="current-password"
      />
           <button
            type="button"
            className={`passwordToggle ${
              passwordVisible &&
              "passwordToggle_visible"
            }`}
            onClick={togglePasswordVisibility}
          />

      </div>

      {(formik.touched.email && formik.errors.email === "VALIDATION_REQUIRED_FIELDS"
        || formik.touched.password && formik.errors.password === "VALIDATION_REQUIRED_FIELDS"
        ) &&
        // (
        //   formik.errors.email === "VALIDATION_REQUIRED_FIELDS" ||
        //   formik.errors.password === "VALIDATION_REQUIRED_FIELDS" 
        // )&&
        (
          <p className={loginStyles.login__invalid_message} data-testid="params-error-by-email">
            {t("VALIDATION_REQUIRED_FIELDS")}
          </p>
        )
      }

      {formik.touched.email && formik.errors.email && formik.errors.email !== "VALIDATION_REQUIRED_FIELDS" && (
        <p className={loginStyles.login__invalid_message} data-testid="login-error-by-email">
          {t(formik.errors.email)}
        </p>
      )}

      {formik.touched.password && formik.errors.password && formik.errors.password !== "VALIDATION_REQUIRED_FIELDS" && (
        <p className={loginStyles.login__invalid_message} data-testid="login-error-by-password">
          {t(formik.errors.password)}
        </p>
      )}
     


      {isError && (
        <p
          className={loginStyles.login__invalid_message}
          data-testid="login-error"
        >
          {loginError}
        </p>
      )}

      {/*<Link*/}
      {/*  to="/restore_password"*/}
      {/*  className={loginStyles.login__restorePasswordLink}*/}
      {/*>*/}
      {/*  שכחת סיסמה?*/}
      {/*</Link>*/}

      <button
        type="submit"
        disabled={
          (countdown !== null && countdown > 0) ||
          !isFormComplete ||
          isLoading ||
          formik.isSubmitting
        }
        className={`button ${loginStyles.login__button} ${(countdown !== null && countdown > 0) ||
          !isFormComplete ||
          isLoading ||
          formik.isSubmitting
          ? loginStyles.login__button_disabled
          : "hover"
          }`}
        data-testid="login-by-email"
      >
        {isLoading ? t('ADD_TREATMENT_PLAN_BUTTON_SENDING') : t("LOG_IN")}{" "}
        {countdown !== null && countdown > 0 ? `(${countdown})` : ""}
      </button>
    </form>
  );
}

export default LoginEmailForm;
